import { withProperties, isPlainObject } from "../../utils";
import createPingHandlerFactory from "./ping";
import EventLog from '../../logger/events'
import {
  getChargeReference,
  getOrderReference,
  getTransactionReference,
  getChargeResponseCode,
  getChargeRedirectUrl,
  traverseDataFields
} from "./helpers";

function __chargeResponseHandlerFactory__(options) {
  return createPingHandlerFactory(function __delegatedHandler__() {
    const { context, options, response } = this;
    const { data } = context;

    const {
      onChargeFailed,
      onChargeSuccessful,
      onRequireDiscount,
      onRequireRedirection,
      onRequireSuggestedAuth,
      onRequireValidation
    } = options;

    if (isPlainObject(data) && 'apply_discount' in data) {
      return onRequireDiscount.call(context);
    }

    if (['AUTH_SUGGESTION', 'auth_suggestion', 'V-COMP'].includes(response.message) && isPlainObject(data) && 'suggested_auth' in data) {
      const { suggested_auth } = data;
      let auth = null;

      switch (suggested_auth) {
        case 'PIN':
        case 'AVS':
        case 'AVS_VBVSECURECODE':
        case 'NOAUTH_INTERNATIONAL':
          auth = ('PIN' === suggested_auth) ? suggested_auth : 'AVS';
          break;
      }

      // Augment the charge callback context with `auth` and `suggested_auth`
      // properties before triggering the `onRequireSuggestedAuth` callback.
      return onRequireSuggestedAuth.call(
        withProperties(context, { auth, suggested_auth })
      );
    }

    // Augment the charge callback context with an object of references:
    // charge(flw_reference), order(order_reference), transaction(txref).
    withProperties(context, {
      reference: withProperties(Object.create(null), {
        charge: getChargeReference(data),
        order: getOrderReference(data),
        transaction: getTransactionReference(data)
      })
    });

    const chargeCode = getChargeResponseCode(data);

    const { chargeStatus, chargeType, paymentType } =
      traverseDataFields(data, 'charge_type::chargeType', 'paymentType', 'status::chargeStatus');

    if (
      ['00'].indexOf(chargeCode) >= 0 ||
      (chargeCode === 'pending-capture' && paymentType === 'card' && chargeType === 'preauth')
    ) { return onChargeSuccessful.call(context) }

    if (chargeCode === '02') {
      EventLog.chargeRequestStatus('successful - Pending verification');

      const { validationCode, authModel, validationType, responseMessage } =
        traverseDataFields(
          data,
          'acctvalrespcode::validationCode',
          'authModelUsed::authModel',
          'type::validationType',
          'response_message::responseMessage'
        );

      // Augment context object with more properties
      withProperties(context, {
        auth: authModel || null,
        charge_code: chargeCode,
        charge_type: chargeType,
        payment_type: paymentType,
        validation_code: validationCode,
        validation_type: validationType
      });

      if (authModel === 'RANDOM_DEBIT' && validationCode === 'RR') {
        EventLog.chargeRequestStatus('failed');

        return onChargeFailed.call(context);
      }

      if (
        ['PIN', 'OTP', 'GTB_OTP', 'ACCESS_OTP', 'MVISA-QR'].indexOf(authModel) >= 0 ||
        ['twofactorvalidate', 'callback', 'paymentcode'].indexOf(validationType) >= 0
      ) { return onRequireValidation.call(context) }

      try {
        const redirectUrl = new URL(getChargeRedirectUrl(data));

        // Augment the charge callback context with a `url` property for the
        // redirect URL before triggering the `onRequireRedirection` callback.
        return onRequireRedirection.call(
          withProperties(context, { url: redirectUrl })
        );
      } catch (e) {
        if (!(validationType && validationType === 'redirect') && (
          chargeStatus === 'pending' ||
          String(chargeStatus).toLowerCase().indexOf('pending') >= 0 ||
          String(responseMessage).toLowerCase().indexOf('in progress') >= 0
        )) { return onRequireValidation.call(context) }

        return Promise.reject(new Error('charge.redirect.error'));
      }
    }

    return onChargeFailed.call(context);
  })(options);
}

export default function __handleChargeResponse__(response) {
  const {
    onBadResponse,
    onChargeFailed,
    onChargeSuccessful,
    onDataResponse,
    onRequireDiscount,
    onRequireRedirection,
    onRequireSuggestedAuth,
    onRequireValidation
  } = this;

  return __chargeResponseHandlerFactory__({
    // maxPingIterations: 0,
    onBadResponse,
    onChargeFailed,
    onChargeSuccessful,
    onDataResponse,
    onRequireDiscount,
    onRequireRedirection,
    onRequireSuggestedAuth,
    onRequireValidation
  })(response);
}
